import { defu } from "defu";

type FetchOptions = Parameters<typeof $fetch>[1];

interface Optionals {
  isFile?: boolean;
  noAuth?: boolean;
  /** Keep the request running when the route changes. */
  bypassAbort?: boolean;
}

export const isApiAbortError = (error: unknown): boolean => {
  return (
    error instanceof Error &&
    (error.name === "AbortError" || isApiAbortError(error.cause))
  );
};

const appendFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;

  if (Array.isArray(value)) {
    value.forEach((item) => appendFormValue(formData, key, item));
    return;
  }

  if (value instanceof Blob) {
    formData.append(key, value);
    return;
  }

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }

  if (typeof value === "string") {
    formData.append(key, value);
    return;
  }

  formData.append(key, `${value}`);
};

const buildFormData = (body: unknown) => {
  if (body instanceof FormData) return body;

  const formData = new FormData();

  if (!body || typeof body !== "object") return formData;

  Object.entries(body).forEach(([key, value]) => {
    appendFormValue(formData, key, value);
  });

  return formData;
};

const isRefreshing = ref<boolean>(false);
const isLoggingOut = ref<boolean>(false);

const waitForRefreshing = async (checkInterval = 1000) => {
  // for loop wait for refresh token
  return new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (!isRefreshing.value) {
        clearInterval(interval);
        resolve();
      }
    }, checkInterval);
  });
};

const logout = async () => {
  const nuxtApp = useNuxtApp();
  const authStore = useAuthStore();
  const { type: userType } = storeToRefs(authStore);

  if (isLoggingOut.value) return await nextTick();

  await nuxtApp.runWithContext(async () => {
    try {
      if (!isLoggingOut.value) {
        isLoggingOut.value = true;
        await authStore.logout();
      }
    } catch (err) {
      console.error(err);
    } finally {
      isLoggingOut.value = false;

      await navigateTo({
        name: "auth-user_type-login",
        params: {
          user_type: String(userType.value),
        },
      });
    }
  });
};
/*
 * TO USE THIS COMPOSABLES
 *
 * const data = await useApi(url, option);
 * conssole.log(data) <--- response data
 *
 */

export async function useApi<T>(
  url: string,
  option: FetchOptions = {},
  optional?: Optionals,
) {
  const config = useRuntimeConfig();
  const routeSignal = optional?.bypassAbort
    ? undefined
    : useNuxtApp().$apiRequestSignal?.();
  const signals = [routeSignal, option.signal].filter(
    (signal): signal is AbortSignal => !!signal,
  );
  if (option.timeout) signals.push(AbortSignal.timeout(option.timeout));
  const signal = signals.length ? AbortSignal.any(signals) : undefined;

  /*
    Uncomment if you already use @sidebase/nuxt-auth
  */

  const authStore = useAuthStore();
  const { token } = storeToRefs(authStore);

  const headers = {
    ...(!optional?.isFile
      ? { "Content-Type": "application/json", Accept: "application/json" }
      : {}),
    ...(!!token.value && !(optional?.noAuth || false)
      ? { Authorization: `Bearer ${token.value.access}` }
      : {}),
  };

  const defaults: FetchOptions = {
    baseURL: (config.public.API_BASE_URL as string) || "",

    retryStatusCodes: [401, 403],
    retry: 1,
    retryDelay: 500,

    headers,

    async onResponse(_ctx: any) {},
    async onResponseError({ request, response, options: resOptions }) {
      signal?.throwIfAborted();

      if (
        response.status === 401 &&
        !optional?.noAuth &&
        !(request as string)?.includes("/logout")
      ) {
        if (token.value?.refresh) {
          if (isRefreshing.value) {
            await waitForRefreshing();
          } else {
            try {
              isRefreshing.value = true;
              await authStore.refreshToken();
              await nextTick();
              signal?.throwIfAborted();
              await authStore.whoAmI();
            } catch {
              signal?.throwIfAborted();
              // token expired
              await logout();
            } finally {
              isRefreshing.value = false;
            }
          }

          signal?.throwIfAborted();

          // Set authorization headers directly from here
          resOptions.headers.set(
            "Authorization",
            `Bearer ${token.value?.access}`,
          );
        } else {
          await logout();
        }
      }
    },
  };

  // change query param array to string
  if (option.query) {
    for (const key of Object.keys(option.query)) {
      if (
        typeof option.query[key as keyof typeof option.query] === "object" &&
        option.query[key as keyof typeof option.query].length > 0 &&
        typeof option.query[key as keyof typeof option.query][0] !== "object"
      ) {
        option.query[key as keyof typeof option.query] =
          option.query[key as keyof typeof option.query]?.join(",") || "";
      }
    }
  }

  if (optional?.isFile) {
    option.method = "post";
    option.body = buildFormData(option.body);
  }

  // // for nice deep defaults, please use unjs/defu
  const params = defu(option, defaults);
  params.signal = signal;

  const apiFetch = $fetch.create(params);

  return apiFetch<T>(url);
}
